import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form } from 'semantic-ui-react'
import { PageHeader } from 'components'
import { AppPrimaryButton, FormTextAreaFormField, FormTextFormField } from 'elements'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FormErrorText } from 'utility'
import { CustomerService } from 'utility/services/customer'
import { AuthService } from 'utility/services/auth'

const CustomerSchema = yup.object().shape({
    customer_name: yup
        .string()
        .trim('Customer name does not allow spaces')
        .strict()
        .required('Customer Name is required'),
    Address: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    City: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    State: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    Zip: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    Email: yup
        .string()
        .email('Please enter a valid email address')
        .trim(FormErrorText.SpacesNotAllowed)
        .strict(),
    Phone: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .strict()
        .nullable(),
})

export const ClientSetupScreen = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CustomerSchema),
    })

    // Get user info from AuthService
    const user = AuthService.getUser()
    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId || 1
    const createdBy = user?.unique_id || 1

    const onSubmit = async (formData) => {
        setLoading(true)
        try {
            const payload = {
                ...formData,
                CustomerGroupId: 1, // Default group ID
                CompanyId: companyId,
                CreatedBy: createdBy,
            }
            await CustomerService.add(payload)
            // Navigate to templates page after successful client setup
            navigate('/app/templates')
        } catch (error) {
            console.error('Failed to add customer:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSkip = () => {
        // Allow user to skip and go directly to templates
        navigate('/app/templates')
    }

    return (
        <div className="client-setup-screen px-4 py-6 max-w-4xl mx-auto">
            <PageHeader header="Welcome to EAuditor!" />

            <div className="mt-6 mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    Set Up Your First Client/Site
                </h2>
                <p className="text-gray-600 mb-6">
                    To get started with inspections, you'll need to set up at least one client or site.
                    This will allow you to assign inspections to specific locations or organizations.
                </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-medium text-gray-800 mb-6">
                    Client/Site Information
                </h3>

                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            label="Customer Name"
                            name="customer_name"
                            placeholder="Customer Name"
                            errors={errors?.customer_name}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="Email"
                            name="Email"
                            placeholder="Email"
                            errors={errors?.Email}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="Phone"
                            name="Phone"
                            placeholder="Phone"
                            errors={errors?.Phone}
                            defaultValue=""
                            type="number"
                        />
                    </Form.Group>

                    <FormTextAreaFormField
                        control={control}
                        label="Address"
                        name="Address"
                        placeholder="Address"
                        errors={errors?.Address}
                        defaultValue=""
                        rows={5}
                    />

                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            label="City"
                            name="City"
                            placeholder="City"
                            errors={errors?.City}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="State"
                            name="State"
                            placeholder="State"
                            errors={errors?.State}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="Zip"
                            name="Zip"
                            placeholder="Zip"
                            errors={errors?.Zip}
                            defaultValue=""
                            type="number"
                        />
                    </Form.Group>

                    <div className="flex justify-between items-center mt-8">
                        <button
                            type="button"
                            onClick={handleSkip}
                            className="text-gray-500 hover:text-gray-700 underline"
                        >
                            Skip for now
                        </button>

                        <AppPrimaryButton
                            type="submit"
                            loading={loading}
                            disabled={loading}
                        >
                            Create Client/Site & Continue
                        </AppPrimaryButton>
                    </div>
                </Form>
            </div>

            <div className="mt-6 text-center text-sm text-gray-500">
                <p>
                    You can always add more clients/sites later from the Templates page.
                </p>
            </div>
        </div>
    )
}

export default ClientSetupScreen
